import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

// Annual review: the admin rates each current staff member once per anniversary year,
// during the last month of the year + 7 days. The backend decides which year is rated
// and whether the window is open — nothing is computed here.

const initialState = {
  staff: [], // GET /staff-annual-review/company — one page, status OPEN | REVIEWED | UPCOMING
  staffTotal: 0,
  stats: { open: 0, reviewed: 0, upcoming: 0 }, // whole staff, not just the page
  loading: false,
  history: [], // GET /staff-annual-review/company/history — one page
  historyTotal: 0,
  historyLoading: false,
  submitting: false,
  submitError: null // backend error code, e.g. 'AnnualReviewWindowClosed'
};

const errorCode = (error) => {
  const message = error.response?.data?.message;
  return typeof message === 'string' ? message : 'GenericError';
};

export const fetchAnnualReviewStatus = createAsyncThunk(
  'staffAnnualReview/fetchStatus',
  // params: { page, limit, search }
  async (params, { rejectWithValue, dispatch }) => {
    try {
      return (await api.get('/staff-annual-review/company', { params })).data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(errorCode(error));
    }
  }
);

export const fetchAnnualReviewHistory = createAsyncThunk(
  'staffAnnualReview/fetchHistory',
  // params: { page, limit, search }
  async (params, { rejectWithValue, dispatch }) => {
    try {
      return (await api.get('/staff-annual-review/company/history', { params })).data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(errorCode(error));
    }
  }
);

export const submitAnnualReview = createAsyncThunk(
  'staffAnnualReview/submit',
  async (
    // `criteria` : [{ key, score }], une note par critère de la zone d'activité.
    { employeeId, criteria, comment },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await api.post(`/staff-annual-review/${employeeId}`, {
        criteria,
        comment
      });
      return { employeeId, review: response.data };
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(errorCode(error));
    }
  }
);

const staffAnnualReviewSlice = createSlice({
  name: 'staffAnnualReview',
  initialState,
  reducers: {
    clearAnnualSubmitError: (state) => {
      state.submitError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnnualReviewStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAnnualReviewStatus.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.staff = payload.data || [];
        state.staffTotal = payload.recordsTotal || 0;
        state.stats = payload.stats || initialState.stats;
      })
      .addCase(fetchAnnualReviewStatus.rejected, (state) => {
        state.loading = false;
      })

      .addCase(fetchAnnualReviewHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(fetchAnnualReviewHistory.fulfilled, (state, { payload }) => {
        state.historyLoading = false;
        state.history = payload.data || [];
        state.historyTotal = payload.recordsTotal || 0;
      })
      .addCase(fetchAnnualReviewHistory.rejected, (state) => {
        state.historyLoading = false;
      })

      .addCase(submitAnnualReview.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(submitAnnualReview.fulfilled, (state, { payload }) => {
        state.submitting = false;
        if (state.staff.find((m) => m._id === payload.employeeId)?.status === 'OPEN') {
          state.stats.open = Math.max(state.stats.open - 1, 0);
          state.stats.reviewed += 1;
        }
        state.staff = state.staff.map((member) =>
          member._id === payload.employeeId
            ? {
                ...member,
                status: 'REVIEWED',
                daysLeft: null,
                review: payload.review
              }
            : member
        );
      })
      .addCase(submitAnnualReview.rejected, (state, { payload }) => {
        state.submitting = false;
        state.submitError = payload;
      });
  }
});

export const { clearAnnualSubmitError } = staffAnnualReviewSlice.actions;

export default staffAnnualReviewSlice.reducer;
